import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem // Import from the correct location
} from '@/components/ui/select';
import { useAppSearchParams } from '@/hooks/use-app-search-params';
import { CURRENCIES } from '@/lib/currency';
import { showToast } from '@/components/ui/show-toast';
// import { revalidateVerifyData } from '@/actions/revalidate-data';
import { API_BASE_URL } from '@/constants';

// Define the validation schema with Zod
const donorSchema = z
  .object({
    donors: z
      .array(
        z.object({
          donorName: z.string().min(1, 'Organization name is required'),
          noOfYears: z.string().min(1, 'Number of years is required'),
          projectDescription: z
            .string()
            .min(1, 'Project description is required')
        })
      )
      .min(1, 'Please add at least one donor organization'),
    currentDonor: z.string().optional(),
    referrals: z.string().optional(),
    innovationHubs: z.string().optional(),
    largestInvestment: z.string().optional(),
    currency: z.string().optional()
  })
  .refine(
    (data) => {
      // If either field is provided, both must be provided
      if (data.largestInvestment || data.currency) {
        return !!data.largestInvestment && !!data.currency;
      }
      return true;
    },
    {
      message: 'Both amount and currency must be provided if one is provided',
      path: ['largestInvestment'] // This will show the error on the amount field
    }
  );

type DonorFormValues = z.infer<typeof donorSchema>;

export default function DonorForm() {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<DonorFormValues>({
    resolver: zodResolver(donorSchema),
    defaultValues: {
      donors: [],
      currentDonor: '',
      referrals: '',
      innovationHubs: '',
      largestInvestment: '',
      currency: ''
    }
  });

  const params = useAppSearchParams();
  const token = params.get('token');
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'donors'
  });

  const onSubmit = async (data: DonorFormValues) => {
    const payload = {
      type: 'Yes',
      currentDonorOrganization: data.currentDonor,
      serviceReferral: data.referrals,
      endorsementProficiencies: data.innovationHubs,
      largestInvestment: data.largestInvestment,
      currency: data.currency,
      donorList: data.donors,
      questions: []
    };

    try {
      setIsSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/experience`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to update Experience');
      }

      showToast('success', 'Experience updated successfully');
      //await revalidateVerifyData();
      navigate('/verify/success');
    } catch (error) {
      showToast('error', 'Failed to update company information');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='space-y-8'>
      <form onSubmit={handleSubmit(onSubmit)} className='space-y-10'>
        <div className='space-y-6'>
          <h2 className='text-2xl font-bold text-slate-900'>
            Add donor organisations you have worked with in the past.
          </h2>

          <div>
            {fields.length > 0 && (
              <div className='mb-6 space-y-6'>
                <div className='grid grid-cols-[1fr_1fr_1fr_auto] gap-4 font-semibold text-slate-900'>
                  <div>Donor Organization Name</div>
                  <div>Number of Years</div>
                  <div>Project Description</div>
                  <div></div>
                </div>
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className='grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-4'
                  >
                    <div>
                      <Input
                        {...register(`donors.${index}.donorName`)}
                        placeholder='Organization name'
                      />
                      {errors.donors?.[index]?.donorName && (
                        <p className='mt-1 text-sm text-red-500'>
                          {errors.donors[index]?.donorName?.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Input
                        {...register(`donors.${index}.noOfYears`)}
                        type='number'
                        placeholder='Years'
                      />
                      {errors.donors?.[index]?.noOfYears && (
                        <p className='mt-1 text-sm text-red-500'>
                          {errors.donors[index]?.noOfYears?.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Input
                        {...register(`donors.${index}.projectDescription`)}
                        placeholder='Description'
                      />
                      {errors.donors?.[index]?.projectDescription && (
                        <p className='mt-1 text-sm text-red-500'>
                          {errors.donors[index]?.projectDescription?.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='text-red-500 hover:bg-red-50 hover:text-red-700'
                        onClick={() => remove(index)}
                      >
                        <Trash2 className='h-5 w-5' />
                        <span className='sr-only'>Remove donor</span>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div>
              <Button
                type='button'
                variant='outline'
                className='border-[#283474] text-[#283474] hover:bg-[#283474]/10'
                onClick={() =>
                  append({
                    donorName: '',
                    noOfYears: '',
                    projectDescription: ''
                  })
                }
              >
                <Plus className='mr-2 h-4 w-4' /> Add donor
              </Button>
              {errors.donors && !Array.isArray(errors.donors) && (
                <p className='mt-2 text-sm text-red-500'>
                  {errors.donors.message}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className='space-y-4'>
          <h2 className='text-lg font-semibold text-slate-900 md:text-2xl'>
            Which donor organization are you currently working closely with?
          </h2>
          <Textarea
            {...register('currentDonor')}
            className='min-h-[120px] w-full rounded-md bg-slate-100 p-4'
            placeholder='Which donor organization are you currently working closely with?'
          />
          {errors.currentDonor && (
            <p className='text-sm text-red-500'>
              {errors.currentDonor.message}
            </p>
          )}
        </div>

        <div className='space-y-4'>
          <h2 className='text-lg font-semibold text-slate-900 md:text-2xl'>
            Do you have referral for your services?
          </h2>
          <p className='text-slate-500'>
            This question helps the Foundation to verify the projects or
            capabilities of your organization.
          </p>
          <Textarea
            {...register('referrals')}
            className='min-h-[120px] w-full rounded-md bg-slate-100 p-4'
            placeholder='List name(s) of organization(s) that can provide referral for your services.'
          />
          {errors.referrals && (
            <p className='text-sm text-red-500'>{errors.referrals.message}</p>
          )}
        </div>

        <div className='space-y-4'>
          <h2 className='text-lg font-semibold text-slate-900 md:text-2xl'>
            Are you a part of an innovation hub or geospatial data community who
            can vouch for your organization&apos;s capabilities?
          </h2>
          <Textarea
            {...register('innovationHubs')}
            className='min-h-[120px] w-full rounded-md bg-slate-100 p-4'
            placeholder="List any innovation hubs or geospatial data communities that can vouch for your organization's capabilities."
          />
          {errors.innovationHubs && (
            <p className='text-sm text-red-500'>
              {errors.innovationHubs.message}
            </p>
          )}
        </div>

        <div className='space-y-4'>
          <h2 className='text-lg font-semibold text-slate-900 md:text-2xl'>
            What is the largest investment you have received for a project?
          </h2>
          <div className='flex gap-4'>
            <div className='w-1/4'>
              <Select
                onValueChange={(value) => setValue('currency', value)}
                value={watch('currency') || ''}
              >
                <SelectTrigger className='h-12'>
                  <SelectValue placeholder='Select currency' />
                </SelectTrigger>
                <SelectContent className='max-h-[300px] overflow-y-auto'>
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.currency && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.currency.message}
                </p>
              )}
            </div>
            <div className='flex-1'>
              <Input
                {...register('largestInvestment')}
                type='number'
                placeholder='Amount'
                className='w-full'
              />
              {errors.largestInvestment && (
                <p className='mt-1 text-sm text-red-500'>
                  {errors.largestInvestment.message}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className='flex justify-end'>
          <Button
            type='submit'
            disabled={isSubmitting}
            className='bg-[#283474] px-8 py-3 text-white hover:bg-[#1e2859]'
          >
            {isSubmitting ? 'Submitting...' : 'Continue'}
          </Button>
        </div>
      </form>
    </div>
  );
}
